import React, { useState, useCallback, memo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, DatePicker, Radio } from 'antd';
import { TextField, Button } from 'react-md';
import moment from "moment";

import RangePicker from '../../Common/DatePicker/RangePicker';
import person from '../../../assets/images/common/person.svg';
import arrow from '../../../assets/images/common/black-arrow.svg';
import { getSpaceAvailability } from './../HostDashboardActions';
import { CALENDER_DATE_FORMAT } from '../../../settings';
import {DATE_FORMAT_REVERSE, DAILY, EDIT, CREATE, SEATING_ARRANGEMENT, BLOCK, WEEKLY, MONTHLY} from '../../Common/constants';
import { getDatesBetween } from './../../../helper/RangePickerHelper';
import { DATE_FORMAT } from './../../Common/constants';
import SingleDatePicker from '../../Common/DatePicker/SingleDatePicker';
import ReservationLoader from '../../Common/Loaders/ReservationLoader';

const CreateReservationWorkModal = memo(({
  setVisible,
  space,
  blockType,
  onProceed,
  previousBookingData,
  selectedDateOnCalender
}) => {
  const dispatch = useDispatch();

  const [selectedDate, setSelectedDate] = useState([]);
  const [duration, setDuration] = useState(1);
  const [datePickers, setDatePickers] = useState([`${moment().format('L')} - ${moment().format('L')}`]);
  const [range, setRange] = useState(duration);
  const [viewGuestCount, setViewGuestCount] = useState(1);
  const events = space?.events
  const [maxGuest, setMaxGuestCount] = useState(space && space.events && space.events.length ? events[0].seatArrangements[0]?.max : 50)
  const chargingTypes = space.events && space.events.length && space.events[0]?.chargings || [];

  const [paymentType, setPaymentType] = useState(chargingTypes?.[0]?.chargingType?.name || DAILY);

  const [bookingDetails, setBookingDetails] = useState({});
  const [guestDetails, setGuestDetails] = useState({});
  const [guestCountErrorMessage, setGuestCountErrorMessage] = useState(false);
  const [buttonDisplayStatus, setButtonDisplayStatus] = useState(true)
  const availableDates = useSelector(state => state?.hostDashboard?.availability?.data || []);
  const selectedSpaceData = useSelector(state => state?.hostDashboard?.data?.selectedSpaceData || []);

  const isFetchingAvailableDates = useSelector(state => state?.hostDashboard?.availability?.data || []);

  const [startDate, setStartDate] = useState(() => {
    if (blockType === EDIT && previousBookingData?.startingDate) {
      return moment(previousBookingData.startingDate, moment.defaultFormat).format('YYYY-MM-DD');
    } else if (blockType === CREATE && selectedDateOnCalender) {
      return moment(selectedDateOnCalender, moment.defaultFormat).format('YYYY-MM-DD');
    }
    return moment().format('YYYY-MM-DD');
  });
  
  const [calculatedEndDate, setCalculatedEndDate] = useState(null);

  const startAvailabilityDate =  moment().format(DATE_FORMAT_REVERSE);
  const endAvailabilityDate =  moment().add(3, 'months').format(DATE_FORMAT_REVERSE);
  
  const [unavailableDatesMessage, setUnavailableDatesMessage] = useState('');
  const [dateRangeAvailability, setDateRangeAvailability] = useState({ available: true, minGuests: 0 });

  // Calculate duration for EDIT mode
  const calculateDurationFromBookingData = useCallback(() => {
    if (blockType === EDIT && previousBookingData?.startingDate && previousBookingData?.endingDate) {
      const startMoment = moment(previousBookingData.startingDate, moment.defaultFormat);
      const endMoment = moment(previousBookingData.endingDate, moment.defaultFormat);
      
      if (paymentType === WEEKLY) {
        const weeks = endMoment.diff(startMoment, 'weeks') + 1; // +1 because end date is inclusive
        return weeks;
      } else if (paymentType === MONTHLY) {
        const months = endMoment.diff(startMoment, 'months') + 1; // +1 because end date is inclusive
        return months;
      }
    }
    return 1;
  }, [blockType, previousBookingData, paymentType]);

  const checkDateRangeAvailability = (startDate, duration, paymentType) => {
  if (paymentType === DAILY) return { available: true, minGuests: maxGuest };
  
  const dateRange = [];
  const unavailableDates = [];
  const availableGuestCounts = [];
  
  // Generate date range based on payment type
  for (let i = 0; i < duration; i++) {
    let currentDate;
    if (paymentType === WEEKLY) {
      // For weekly, we need to check each day in each week
      for (let week = 0; week < duration; week++) {
        for (let day = 0; day < 7; day++) {
          currentDate = moment(startDate).add(week, 'weeks').add(day, 'days').format('YYYY-MM-DD');
          dateRange.push(currentDate);
        }
      }
      break;
    } else if (paymentType === MONTHLY) {
      // For monthly, we need to check each day in each month
      const monthStart = moment(startDate).add(i, 'months');
      const monthEnd = monthStart.clone().endOf('month');
      const daysInMonth = monthEnd.date();
      
      for (let day = 0; day < daysInMonth; day++) {
        currentDate = monthStart.clone().add(day, 'days').format('YYYY-MM-DD');
        dateRange.push(currentDate);
      }
    }
  }
  const uniqueDates = [...new Set(dateRange)];
  
    uniqueDates.forEach(date => {
      const available = findAvailableDate(date);
      const dateKey = `${moment(date).format(DATE_FORMAT)}T00:00:00.000+0000`;
      
      if (!available || !available[dateKey] || !available[dateKey].status) {
        unavailableDates.push(moment(date).format('MMM DD, YYYY'));
      } else {
        availableGuestCounts.push(available[dateKey].availableSeats);
      }
    });
    
    const minGuestCount = availableGuestCounts.length > 0 
      ? Math.min(...availableGuestCounts) 
      : 0;
    
    return {
      available: unavailableDates.length === 0,
      unavailableDates,
      minGuests: minGuestCount,
      totalDatesChecked: uniqueDates.length
    };
  };

  useEffect(() => {
  if ((paymentType === WEEKLY || paymentType === MONTHLY) && startDate && duration > 0) {
    const availabilityCheck = checkDateRangeAvailability(startDate, duration, paymentType);
    
    setDateRangeAvailability(availabilityCheck);
    
    if (!availabilityCheck.available) {
      const unavailableDatesStr = availabilityCheck.unavailableDates.slice(0, 3).join(', ');
      const remainingCount = availabilityCheck.unavailableDates.length - 3;
      const message = `Some dates are unavailable: ${unavailableDatesStr}${remainingCount > 0 ? ` and ${remainingCount} more` : ''}. Please select a different date range.`;
      setUnavailableDatesMessage(message);
      setMaxGuestCount(0);
    } else {
      setUnavailableDatesMessage('');
      setMaxGuestCount(availabilityCheck.minGuests);
      
      if (availabilityCheck.minGuests < space?.events?.[0]?.seatArrangements?.[0]?.max) {
        setUnavailableDatesMessage(`Guest limit is ${availabilityCheck.minGuests} for this ${paymentType.toLowerCase()} period due to availability.`);
      }
    }
  }
}, [startDate, duration, paymentType, availableDates]);

  useEffect(() => {
    if(space && space.events && space.events.length > 0 && space.events[0].eventType.spaceCategory.name === 'Work'){
      const initialChargingType = chargingTypes?.[0]?.chargingType?.name || DAILY;
      setPaymentType(initialChargingType);
      dispatch(getSpaceAvailability({
        spaceId: space.id, 
        eventTypeId: space.events[0].eventType.id, 
        startDate: startAvailabilityDate, 
        endDate: endAvailabilityDate
      }, initialChargingType));

      if(blockType === EDIT){
        const filteredChargingTypes = chargingTypes?.filter(chargingType => 
          chargingType.chargingType.name === selectedSpaceData.chargingType
        );
        setPaymentType(selectedSpaceData.chargingType || initialChargingType);
      }
    }
  }, [space]);

  useEffect(() => {
    if (space && space.events && space.events.length > 0 && space.events[0].eventType.spaceCategory.name === 'Work') {
      dispatch(getSpaceAvailability({
        spaceId: space.id, 
        eventTypeId: space.events[0].eventType.id, 
        startDate: startAvailabilityDate, 
        endDate: endAvailabilityDate
      }, paymentType));
    }
  }, [paymentType]);

  useEffect(() => {
    if(previousBookingData && previousBookingData.startingDate && previousBookingData.endingDate && availableDates)
      setMinSeatForSelectedDate(
        getDatesBetween(
          moment(previousBookingData.startingDate, moment.defaultFormat).toDate(),
          moment(previousBookingData.endingDate, moment.defaultFormat).toDate()
        ), previousBookingData.guestCount
      );
  }, [availableDates]);

  useEffect(() => {
    if (previousBookingData) {
      setBookingDetails({
        title: previousBookingData?.title || "",
        description: previousBookingData?.description || ""
      });
      
      setGuestDetails({
        firstName: previousBookingData?.firstName || "",
        lastName: previousBookingData?.lastName || "",
        contact: previousBookingData?.contact || "",
        email: previousBookingData?.email || ""
      });
      
      setViewGuestCount(previousBookingData?.guestCount || "");

      if (blockType === EDIT && previousBookingData.chargingType) {
        setPaymentType(previousBookingData.chargingType);
      }

      // Set dates and duration based on previous booking data
      if (previousBookingData.startingDate && previousBookingData.endingDate) {
        const startMoment = moment(previousBookingData.startingDate, moment.defaultFormat);
        const endMoment = moment(previousBookingData.endingDate, moment.defaultFormat);
        const formattedStartDate = startMoment.format('YYYY-MM-DD');
        
        setStartDate(formattedStartDate);
        
        // Calculate duration for weekly/monthly
        if (paymentType === WEEKLY) {
          const weeks = Math.ceil(endMoment.diff(startMoment, 'days') / 7);
          setDuration(weeks);
          setCalculatedEndDate(endMoment);
        } else if (paymentType === MONTHLY) {
          const months = endMoment.diff(startMoment, 'months') + 1;
          setDuration(months);
          setCalculatedEndDate(endMoment);
        } else if (paymentType === DAILY) {
          // Handle daily reservations
          setSelectedDate([
            getDatesBetween(startMoment.toDate(), endMoment.toDate())
          ]);
        }
      }
    }
    
    //create reservation
    if (blockType === CREATE && selectedDateOnCalender) {
      const selectedMoment = moment(selectedDateOnCalender, moment.defaultFormat);
      const formattedDate = selectedMoment.format('YYYY-MM-DD');
      
      if (paymentType === DAILY) {
        setSelectedDate([
          getDatesBetween(selectedMoment.toDate(), selectedMoment.toDate())
        ]);
        setMinSeatForSelectedDate(
          getDatesBetween(selectedMoment.toDate(), selectedMoment.toDate()), 
          0
        );
      } else {
        setStartDate(formattedDate);
      }
    }
  }, [blockType, previousBookingData, selectedDateOnCalender, paymentType]);

  const handleGuestChange = value => {
    setGuestCountErrorMessage(false);
    const maxAllowed = dateRangeAvailability.available ? maxGuest : 0;
    
    if (value > 0 && value <= maxAllowed) {
      setViewGuestCount(value);
    } else if (value > maxAllowed && maxAllowed > 0) {
      setViewGuestCount(maxAllowed);
    }
  };

  const findAvailableDate = (date) => {
    return availableDates.find(availableDate => {
      const [ selecteAvailableDate ] = Object.keys(availableDate);
      if(availableDate[`${moment(date).format(DATE_FORMAT)}T00:00:00.000+0000`] &&
      `${moment(date).format(DATE_FORMAT)}T00:00:00.000+0000` === selecteAvailableDate )
          return availableDate[selecteAvailableDate];
      });
  }

  const handleRangeSelectedDate = (date) => {
    setSelectedDate(date);
    setAvailableGuestCount();
  }

  const setMinSeatForSelectedDate = (dateRange, previousBookingGuestCount = 0) => {
    const availableSeatCountArray = [];
    dateRange.forEach(selDate => {
      const available = findAvailableDate(selDate);
      if(available && available[`${moment(selDate).format(DATE_FORMAT)}T00:00:00.000+0000`]){
        available[`${moment(selDate).format(DATE_FORMAT)}T00:00:00.000+0000`]?.status &&
        availableSeatCountArray.push(available[`${moment(selDate).format(DATE_FORMAT)}T00:00:00.000+0000`].availableSeats)
      }
    });

    const minSeatCount = availableSeatCountArray.length ? (Math.min.apply(Math, availableSeatCountArray) + previousBookingGuestCount) : previousBookingGuestCount;

    if(availableDates.length > 0) {
      setGuestCountErrorMessage(viewGuestCount > minSeatCount);
    }
    setMaxGuestCount(minSeatCount);
  }

  const setAvailableGuestCount = () => {
      (selectedDate.length && selectedDate[0].constructor === Array) && (setMinSeatForSelectedDate(selectedDate[0],getPreviousGuestCount(selectedDate[0])));
  }

  const getPreviousGuestCount = (selectedDate) => {
    if(blockType === EDIT && previousBookingData.startingDate === `${moment(selectedDate[0]).format(DATE_FORMAT)}T00:00:00.000+0000` && previousBookingData.endingDate === `${moment(selectedDate[selectedDate.length - 1]).format(DATE_FORMAT)}T00:00:00.000+0000`){
      return previousBookingData.guestCount;
    }else{
      return 0;
    }
  }

  // Handle duration change for weekly/monthly
  const handleDurationChange = (value) => {
    let maxDuration = paymentType === MONTHLY ? 12 : paymentType === WEEKLY ? 52 : 10;
    if (value >= 0 && value <= maxDuration) {
      setDuration(value);
      
      if (value > 0 && startDate) {
        if (paymentType === WEEKLY) {
          const endDateValue = moment(startDate).add(value, 'weeks').subtract(1, 'days');
          setCalculatedEndDate(endDateValue);
        } else if (paymentType === MONTHLY) {
          const endDateValue = moment(startDate).add(value, 'months').subtract(1, 'days');
          setCalculatedEndDate(endDateValue);
        }
      } else {
        setCalculatedEndDate(null);
      }
    }
  };

  // Handle single date change for weekly/monthly
  const handleSingleDateChange = (date) => {
    const formattedDate = moment(date).format('YYYY-MM-DD');
    setStartDate(formattedDate);
    
    if (duration > 0) {
      if (paymentType === WEEKLY) {
        const endDateValue = moment(date).add(duration, 'weeks').subtract(1, 'days');
        setCalculatedEndDate(endDateValue);
      } else if (paymentType === MONTHLY) {
        const endDateValue = moment(date).add(duration, 'months').subtract(1, 'days');
        setCalculatedEndDate(endDateValue);
      }
    }
  };


  const handleOnProceed = () => {
    const selectedChargingType = chargingTypes?.find(chargingType => 
      chargingType.chargingType.name === paymentType
    );

    let startDateForPayload, endDateForPayload;

    if (paymentType === DAILY) {
      startDateForPayload = datePickers.length ? moment(datePickers[0].split('-')[0].trim(),'MM/DD/YYYY').format(CALENDER_DATE_FORMAT) : '';
      endDateForPayload = datePickers.length ? moment(datePickers[0].split('-')[1].trim(),'MM/DD/YYYY').format(CALENDER_DATE_FORMAT) : '';
    } else if (paymentType === WEEKLY) {
      startDateForPayload = moment(startDate).format(CALENDER_DATE_FORMAT);
      endDateForPayload = calculatedEndDate ? moment(calculatedEndDate).format(CALENDER_DATE_FORMAT) : moment(startDate).add(duration, 'weeks').subtract(1, 'days').format(CALENDER_DATE_FORMAT);
    } else if (paymentType === MONTHLY) {
      startDateForPayload = moment(startDate).format(CALENDER_DATE_FORMAT);
      endDateForPayload = calculatedEndDate ? moment(calculatedEndDate).format(CALENDER_DATE_FORMAT) : moment(startDate).add(duration, 'months').subtract(1, 'days').format(CALENDER_DATE_FORMAT);
    } else {
      startDateForPayload = moment(startDate).format(CALENDER_DATE_FORMAT);
      endDateForPayload = moment(startDate).format(CALENDER_DATE_FORMAT);
    }

    const payload = {
      id: selectedSpaceData?.id,
      space: space.id,
      event: {
        eventType: events[0]?.eventType?.name,
        eventTypeId: events[0]?.eventType?.id,
        guestCount: viewGuestCount,
        startDate: startDateForPayload,
        endDate: endDateForPayload,
        chargingTypeId: selectedChargingType?.chargingType?.id,
        duration: duration,
        blocks: [
          {
            name: selectedChargingType?.pricingOptions?.[0]?.name || paymentType,
            startTime: selectedChargingType?.pricingOptions?.[0]?.startTime || null,
            endTime: selectedChargingType?.pricingOptions?.[0]?.endTime || null
          }
        ],
      },
      details: {
        title: bookingDetails.title,
        description: bookingDetails.description
      },
      guest: {
        firstName: guestDetails.firstName,
        lastName: guestDetails.lastName,
        contactNumber: guestDetails.contact,
        email: guestDetails.email
      }
    }
    onProceed(payload)
    setGuestDetails({
      firstName : '',
      lastName : '',
      contact : '',
      email : ''
    })
    setBookingDetails({
      title : '',
      description : ''
    })
  };

  const handleBack = () => {
    setVisible(false);
  };

  const handleDateChange = (dateIndex, date) => {
    const filteredList = datePickers.map((other, index) => {
      if (index === dateIndex) {
        return date;
      } else {
        return other;
      }
    });
    setDatePickers(filteredList);
    const dates = [].concat(...selectedDate);
    setRange(dates.length);
  };

  const handleBookingDetailsOnChange = useCallback((name, value) => {
    setBookingDetails(b => {
      return { ...b, [name]: value };
    });
  }, []);

  const handleGuestDetailsOnChange = useCallback((name, value) => {
    setGuestDetails(b => {
      return { ...b, [name]: value };
    });
  }, []);

  const validateEmail = (email) => /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,10})+$/.test(email);

  useEffect(() => {
    if (!bookingDetails.title) {
      setButtonDisplayStatus(true);
    } else if (guestDetails.email.length > 0 && !validateEmail(guestDetails.email)) {
      setButtonDisplayStatus(true);
    } else if ((paymentType === WEEKLY || paymentType === MONTHLY) && !dateRangeAvailability.available) {
      setButtonDisplayStatus(true);
    } else if ((paymentType === WEEKLY || paymentType === MONTHLY) && duration < 1) {
      setButtonDisplayStatus(true);
    } else {
      setButtonDisplayStatus(false);
    }
  }, [bookingDetails.title, availableDates, guestDetails, dateRangeAvailability, paymentType, duration]);


  const getFilteredChargingTypes = () => {
    if (blockType === EDIT) {
      return chargingTypes?.filter(chargingType => 
        chargingType.chargingType.name === selectedSpaceData.chargingType
      ) || [];
    }
    return chargingTypes;
  };

  // Handle payment type change
  const handlePaymentTypeChange = (newPaymentType) => {
    setPaymentType(newPaymentType);
    
    if (blockType !== EDIT || newPaymentType !== (previousBookingData?.chargingType || selectedSpaceData?.chargingType)) {
      setDuration(1);
      setCalculatedEndDate(null);
      setGuestCountErrorMessage(false);
      setUnavailableDatesMessage('');
      setDateRangeAvailability({ available: true, minGuests: 0 });

      if (newPaymentType === DAILY) {
        setDatePickers([`${moment().format('L')} - ${moment().format('L')}`]);
      } else {
        setDatePickers([]);
        setStartDate(moment().format('YYYY-MM-DD'));
      }
    }
  };

  return (
    <>
      <div className="fixed-header">
        <img src={arrow} alt="arrow" className="arrow-img" onClick={() => handleBack()} />
        <div className="text">{space.spaceName}</div>
      </div>
      <div className="content">
        <span className="modal-title">{blockType} reservation</span>
        <div className="radio-btn-wrapper input-margin-wrapper">
          <Radio.Group
            onChange={e => {
              handlePaymentTypeChange(e.target.value);
            }}
            value={paymentType}
            buttonStyle="solid"
          >
            <div className="radio-btn-inner-wrapper">
              {getFilteredChargingTypes()?.map(type => (
                <Radio.Button key={type.chargingType.id} value={type?.chargingType?.name}>
                  {type?.chargingType?.name}
                </Radio.Button>
              ))}
            </div>
          </Radio.Group>
        </div>
        <TextField
          className="guest-input input-margin-wrapper"
          id="guest"
          min="1"
          leftIcon={<img src={person} className="img" alt="person" />}
          label="Guest"
          type="number"
          onChange={(value) => handleGuestChange(value)}
          value={viewGuestCount}
        />
        {isFetchingAvailableDates.length > 0 &&
        <>
          {guestCountErrorMessage && <span className="error"> Sorry! Max availability is {maxGuest} for the selected date(s) </span>}
          {unavailableDatesMessage && (
            <div className={`availability-message input-margin-wrapper ${dateRangeAvailability.available ? 'info-message' : 'error-message'}`}>
              <span className={dateRangeAvailability.available ? 'info' : 'error'}>
                {unavailableDatesMessage}
            </span>
          </div>
        )}
        </>
      }
        {/* Date picker section - different for different payment types */}
        {isFetchingAvailableDates.length > 0 ? (
        <>
        {paymentType === DAILY ? (
          datePickers.map((picker, index) => (
            <div className="date-block-wrapper" key={index}>
              <div className="minus-btn-wrapper input-margin-wrapper" key={index}>
                <RangePicker
                  className="date-select"
                  key={index}
                  index={index}
                  setDuration={setDuration}
                  setSelectedDate={handleRangeSelectedDate}
                  selectedDate={selectedDate}
                  handleDateChange={handleDateChange}
                  dateString={picker}
                  availableDates={availableDates}
                  formFrom={'EDIT_RESERVATION'}
                  blockType={blockType}
                  urlUpdate={false}
                />
              </div>
            </div>
          ))
        ) : (
          <div className="date-block-wrapper">
            <div className="input-margin-wrapper">
              <SingleDatePicker
                selectedDate={startDate}
                label="Start Date"
                minDate={moment().toDate()}
                onChange={handleSingleDateChange}
                availableDates={availableDates}
                value={startDate}
                blockType={blockType}
                urlUpdate={false}
              />
            </div>
            
            {(paymentType === WEEKLY || paymentType === MONTHLY) && (
              <div className="input-margin-wrapper">
                <TextField
                  id="duration"
                  type="number"
                  min="1"
                  max={paymentType === MONTHLY ? 12 : 52}
                  label={paymentType === WEEKLY ? '# of Weeks' : '# of Months'}
                  onChange={(value) => handleDurationChange(value)}
                  value={duration}
                />
              </div>
            )}
            
            {(paymentType === WEEKLY || paymentType === MONTHLY) && calculatedEndDate && (
              <div className="date-range-display input-margin-wrapper">
                <strong>Reservation period:</strong> {moment(startDate).format('MMM DD, YYYY')} - {moment(calculatedEndDate).format('MMM DD, YYYY')}
              </div>
            )}
          </div>
        )}
        </>) : (<ReservationLoader />)}
      </div>
      {(paymentType === DAILY ? selectedDate : startDate) && (
        <>
          <div className="content custom-margin">
            <p className="sub-text">Booking Details</p>
            <TextField
              id="title"
              className="input-margin-wrapper"
              label="Title"
              lineDirection="center"
              required
              errorText="This field is required."
              onChange={value => handleBookingDetailsOnChange('title', value)}
              value={bookingDetails.title}
            />
            <TextField
              id="description"
              className="input-margin-wrapper description-wrapper"
              label="Description"
              lineDirection="right"
              rows={3}
              placeholder=""
              onChange={value => handleBookingDetailsOnChange('description', value)}
              value={bookingDetails.description}
            />
            <p className="sub-text">Guest Personal Details</p>
            <div className="name-wrap left-wrap">
              <TextField
                id="firstName"
                className="input-margin-wrapper"
                label="First Name"
                lineDirection="right"
                placeholder=""
                onChange={value => handleGuestDetailsOnChange('firstName', value)}
                value={guestDetails.firstName}
              />
            </div>
            <div className="name-wrap right-wrap">
              <TextField
                id="lastName"
                className="input-margin-wrapper"
                label="Last Name"
                lineDirection="right"
                placeholder=""
                onChange={value => handleGuestDetailsOnChange('lastName', value)}
                value={guestDetails.lastName}
              />
            </div>
            <TextField
              id="contact"
              className="input-margin-wrapper"
              label="Contact Number"
              lineDirection="right"
              placeholder=""
              onChange={value => handleGuestDetailsOnChange('contact', value)}
              value={guestDetails.contact}
            />
            <TextField
              id="email"
              className="input-margin-wrapper"
              label="Email"
              typr='email'
              lineDirection="right"
              placeholder=""
              onChange={value => handleGuestDetailsOnChange('email', value)}
              value={guestDetails.email}
            />
          </div>
          <div className="footer" >
            <button 
              className="btn-yellow btn-reservation" 
              swapTheming 
              disabled={buttonDisplayStatus || (paymentType !== DAILY && duration < 1)} 
              onClick={handleOnProceed}
            >
              {blockType} reservation
            </button>
          </div>
        </>
      )}
    </>
  )
})

export default CreateReservationWorkModal;